// mui
import {
  Avatar,
  Button,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';

// types
import { OrganiseMember, RecurrenceFrequency } from '@karehero/models';

// components
import { AssigneeSelect } from '@/pwa/molecules/AssigneeSelect/AssigneeSelect';
import { RecurrenceControls } from '@/pwa/molecules/RecurrenceControls/RecurrenceControls';
import {
  ActionAttachments,
  ActionAttachment,
} from '@/pwa/molecules/ActionAttachments/ActionAttachments';

export interface ActionFormCareCircle {
  id: string;
  name: string;
  /** Avatar image URL; falls back to `initials` then a default avatar. */
  avatarUrl?: string;
  /** Pre-computed initials (the wiring supplies these — see pwa getInitials). */
  initials?: string;
}

/**
 * The form's editable state. `date`/`time` are the raw native-input strings
 * (YYYY-MM-DD / HH:MM) — the wiring owns conversion to/from an ISO timestamp.
 */
export interface ActionFormValues {
  careCircleId: string;
  title: string;
  assigneeAccountId: string | null;
  isRecurring: boolean;
  recurrenceFrequency: RecurrenceFrequency;
  recurrenceInterval: number;
  date: string;
  time: string;
}

export interface ActionFormProps {
  mode: 'create' | 'edit';
  value: ActionFormValues;
  /** Patch the form state (the parent merges). */
  onChange: (patch: Partial<ActionFormValues>) => void;
  /** Selectable care circles — the picker shows only when more than one. */
  careCircles: ActionFormCareCircle[];
  /** Members of the selected care circle, for the assignee select. */
  members: OrganiseMember[];
  attachments?: ActionAttachment[];
  /** Triggers the file picker. When omitted the uploads section is hidden. */
  onUploadDocument?: () => void;
  onRemoveAttachment?: (id: string) => void;
  /** When true, the recurrence toggle and controls are shown but disabled (edit-series mode). */
  disableRecurrence?: boolean;
}

/** Whether the form can be submitted. Exported so wiring can gate the footer. */
export const isActionFormValid = (value: ActionFormValues): boolean =>
  !(
    !value.title.trim() ||
    !value.careCircleId ||
    (value.isRecurring && !value.date) ||
    (value.isRecurring &&
      (!value.recurrenceInterval || value.recurrenceInterval < 1))
  );

/** Care-circle picker shown only when organising for more than one circle. */
const CareCircleField = ({
  careCircles,
  value,
  onChange,
}: {
  careCircles: ActionFormCareCircle[];
  value: string;
  onChange: (id: string) => void;
}) => {
  const theme = useTheme();
  const renderCircle = (circle: ActionFormCareCircle) => (
    <Stack
      direction='row'
      spacing={2}
      sx={{
        alignItems: 'center',
      }}
    >
      <Avatar
        alt={circle.name}
        src={circle.avatarUrl}
        sx={
          circle.avatarUrl
            ? undefined
            : {
                bgcolor: theme.palette.custom.marineGreen[900],
                color: theme.palette.custom.marineGreen[100],
              }
        }
      >
        {circle.initials}
      </Avatar>
      <Typography variant='body1'>{circle.name}</Typography>
    </Stack>
  );

  return (
    <FormControl fullWidth>
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        renderValue={(selected) => {
          const circle = careCircles.find((c) => c.id === selected);
          return circle ? renderCircle(circle) : null;
        }}
      >
        {careCircles.map((circle) => (
          <MenuItem key={circle.id} value={circle.id}>
            {renderCircle(circle)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

/** "Repeats" toggle card: title + caption on the left, a switch on the right. */
const RepeatsCard = ({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) => (
  <Paper variant='outlined'>
    <Stack
      direction='row'
      sx={{
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        p: 3,
      }}
    >
      <Stack>
        <Typography variant='body1'>Repeats</Typography>
        <Typography
          variant='body2'
          sx={{
            color: 'text.secondary',
          }}
        >
          Schedule a recurring action
        </Typography>
      </Stack>
      <Switch
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        slotProps={{ input: { 'aria-label': 'Schedule a recurring action' } }}
      />
    </Stack>
  </Paper>
);

/**
 * ActionForm is the carer-facing create/edit action body. The same form serves
 * both modes: a (multi-circle only) care-circle picker, title, assignee, a
 * Repeats toggle revealing recurrence controls, date/time, and an uploads
 * section. Presentational only and dialog-free — the surrounding dialog owns
 * the title and the submit footer (see ActionEditor + the wiring view).
 */
export const ActionForm = ({
  mode,
  value,
  onChange,
  careCircles,
  members,
  attachments = [],
  onUploadDocument,
  onRemoveAttachment,
  disableRecurrence,
}: ActionFormProps) => {
  const uploadButton = onUploadDocument && (
    <Button
      variant='outlined'
      color='inherit'
      size='large'
      fullWidth
      className='dashed'
      onClick={onUploadDocument}
    >
      Upload document
    </Button>
  );

  return (
    <Stack
      spacing={3}
      sx={{
        mt: 1,
      }}
    >
      {careCircles.length > 1 && (
        <CareCircleField
          careCircles={careCircles}
          value={value.careCircleId}
          onChange={(careCircleId) =>
            onChange({ careCircleId, assigneeAccountId: null })
          }
        />
      )}

      <TextField
        autoFocus
        label='Title'
        value={value.title}
        onChange={(e) => onChange({ title: e.target.value })}
        fullWidth
        required
        slotProps={{ inputLabel: { shrink: true } }}
      />

      <AssigneeSelect
        assigneeAccountId={value.assigneeAccountId}
        onAssigneeChange={(assigneeAccountId) =>
          onChange({ assigneeAccountId })
        }
        members={members}
      />

      {(mode === 'create' || disableRecurrence) && (
        <RepeatsCard
          checked={value.isRecurring}
          onChange={(isRecurring) => onChange({ isRecurring })}
          disabled={disableRecurrence}
        />
      )}

      {value.isRecurring && (
        <RecurrenceControls
          frequency={value.recurrenceFrequency}
          onFrequencyChange={(recurrenceFrequency) =>
            onChange({ recurrenceFrequency })
          }
          interval={value.recurrenceInterval}
          onIntervalChange={(recurrenceInterval) =>
            onChange({ recurrenceInterval })
          }
          isDisabled={disableRecurrence}
        />
      )}

      <TextField
        label={
          disableRecurrence
            ? 'Effective from'
            : value.isRecurring
              ? 'Starts'
              : 'Date'
        }
        type='date'
        value={value.date}
        onChange={(e) => onChange({ date: e.target.value })}
        fullWidth
        required={value.isRecurring}
        slotProps={{ inputLabel: { shrink: true } }}
      />
      <TextField
        label='Time'
        type='time'
        value={value.time}
        onChange={(e) => onChange({ time: e.target.value })}
        fullWidth
        slotProps={{ inputLabel: { shrink: true } }}
      />

      {(uploadButton || attachments.length > 0) && (
        <ActionAttachments
          attachments={attachments}
          uploadSlot={uploadButton}
          onRemoveAttachment={onRemoveAttachment}
        />
      )}
    </Stack>
  );
};

ActionForm.displayName = 'ActionForm';
export default ActionForm;
